import { Link as RemixLink } from "@remix-run/react";

import {
  Box,
  Heading,
  Text,
  Button,
  SimpleGrid,
  VStack,
  HStack,
  Link,
} from "@chakra-ui/react";

import { ArrowForwardIcon, CheckCircleIcon } from "@chakra-ui/icons";
import { FaRegCreditCard } from "react-icons/fa";

import { Paper } from "#app/components/index.jsx";

// Free-tier facts mirror the Free plan in
// app/components/pricing/PricingCards/TrialPricingCard.jsx — keep them in sync.
const FREE_FEATURES = [
  "10 cancellation alerts",
  "Instant email notifications",
  "1 campground watched at a time",
];

/**
 * Free-to-try section — the closing CTA on the home, country, and featured
 * landing pages.
 *
 * Reuses the pricing page promise ("Try for free, subscribe for more.") and
 * spells out the free tier so visitors know there's nothing to lose. Partner
 * pages close with the facility-specific CallToAction band instead.
 */
export function FreeToTrySection({ ctaTo = "/new" }) {
  return (
    <Paper
      borderRadius="xl"
      py={{ base: 8, md: 12 }}
      px={{ base: 6, md: 12 }}
      w="100%"
      borderTopWidth="3px"
      borderTopColor="accent"
      data-testid="landing-free-to-try"
    >
      <SimpleGrid
        columns={{ base: 1, md: 2 }}
        spacing={{ base: 8, md: 12 }}
        alignItems="center"
        maxW="container.lg"
        mx="auto"
      >
        {/* Left: the pitch */}
        <Box>
          <Text
            color="accent"
            textTransform="uppercase"
            fontWeight="bold"
            letterSpacing="wide"
            fontSize="sm"
            mb={2}
          >
            Free to try
          </Text>
          <Heading
            as="h2"
            size={{ base: "sm", md: "md" }}
            fontWeight="extrabold"
            letterSpacing="tight"
            lineHeight="1.15"
          >
            Try for free,{" "}
            <Text as="span" display="block" color="warning">
              subscribe for more.
            </Text>
          </Heading>
          <Text color="muted" fontSize={{ base: "md", md: "lg" }} mt={4}>
            Set up your first request in under a minute and we&rsquo;ll email
            you the moment a spot opens up.
          </Text>

          <VStack align="start" spacing={3} mt={6}>
            <Button
              variant="primary"
              size="lg"
              fontWeight="bold"
              as={RemixLink}
              to={ctaTo}
              rightIcon={<ArrowForwardIcon />}
            >
              Start watching for free
            </Button>
            <Link
              as={RemixLink}
              to="/pricing"
              color="muted"
              fontSize="sm"
              fontWeight="medium"
            >
              Want texts or more alerts? See plans &rarr;
            </Link>
          </VStack>
        </Box>

        {/* Right: what's free */}
        <Box
          bg="bg-subtle"
          borderWidth="1px"
          borderColor="border"
          borderRadius="lg"
          p={{ base: 5, md: 6 }}
        >
          <HStack align="baseline" justify="space-between" mb={5}>
            <HStack align="baseline" spacing={1.5}>
              <Text
                fontSize={{ base: "3xl", md: "4xl" }}
                fontWeight="extrabold"
                color="warning"
                lineHeight="1"
              >
                $0
              </Text>
              <Text color="muted" fontWeight="medium">
                to start
              </Text>
            </HStack>
            <Text
              fontSize="xs"
              fontWeight="bold"
              textTransform="uppercase"
              letterSpacing="wide"
              color="accent"
            >
              Free plan
            </Text>
          </HStack>

          <VStack align="stretch" spacing={3}>
            {FREE_FEATURES.map((feature) => (
              <HStack key={feature} align="start" spacing={3}>
                <CheckCircleIcon
                  color="accent"
                  boxSize="18px"
                  mt="2px"
                  flexShrink={0}
                  aria-hidden="true"
                />
                <Text>{feature}</Text>
              </HStack>
            ))}
          </VStack>

          <HStack
            spacing={2}
            mt={5}
            pt={4}
            borderTopWidth="1px"
            borderColor="border"
            color="muted"
            fontSize="sm"
          >
            <Box
              as={FaRegCreditCard}
              boxSize="14px"
              flexShrink={0}
              aria-hidden="true"
            />
            <Text>No credit card required &middot; cancel anytime</Text>
          </HStack>
        </Box>
      </SimpleGrid>
    </Paper>
  );
}
